import { useEffect, useState } from 'react';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import {
  getDefaultPrefs,
  isDeelScriptPresent,
  isGpcActive,
  isPlausibleScriptPresent,
  isSfGtmPresent,
  readConsent,
  saveConsent,
  type ConsentPrefs,
} from '../lib/consent';
import { useViewerCountry } from '../lib/geo';
import { readCloudCacheAnalytics, type CloudCacheState } from '../lib/cloudcache';

const PLAUSIBLE_IGNORE_KEY = 'plausible_ignore';

function readPlausibleIgnore(): boolean {
  try {
    return window.localStorage.getItem(PLAUSIBLE_IGNORE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writePlausibleIgnore(ignore: boolean): void {
  try {
    if (ignore) {
      window.localStorage.setItem(PLAUSIBLE_IGNORE_KEY, 'true');
    } else {
      window.localStorage.removeItem(PLAUSIBLE_IGNORE_KEY);
    }
  } catch {
    /* localStorage blocked - toggle is a best-effort UI */
  }
}

/**
 * Compose the displayed/save baseline for a US viewer with no saved record:
 * start from the geo-aware default, then layer in the State Farm DNS
 * Analytics cookie if it says explicitly denied. Mirrors the precedence
 * that runSfCookieSync uses for the runtime GTM state, so the toggles
 * reflect what GTM is actually doing.
 */
function getEffectiveDefaults(country: string | null): ConsentPrefs {
  const base = getDefaultPrefs(country);
  if (country !== 'US') return base;
  if (readCloudCacheAnalytics() === 'denied') {
    return { ...base, analytics: false };
  }
  return base;
}

type LockSource = 'gpc' | 'sf-cookie' | null;

export default function PrivacySettingsPage() {
  // A stored record (the user has saved a choice, or GPC wrote a deny-all)
  // is the default baseline. With no record, the displayed default is
  // geo-aware: granted for the US opt-out audience, denied for GDPR/UK/CH.
  // country resolves async, so we reconcile in an effect once it lands.
  // For US visitors we also fold in the State Farm DNS Analytics cookie
  // so the displayed toggle matches the runtime GTM state.
  const { country, loading } = useViewerCountry();
  const [stored] = useState<ReturnType<typeof readConsent>>(() => readConsent());
  const initialPrefs = stored?.prefs ?? getEffectiveDefaults(country);

  const [analyticsStorage, setAnalyticsStorage] = useState<boolean>(initialPrefs.analytics);
  const [marketingStorage, setMarketingStorage] = useState<boolean>(initialPrefs.marketing);
  const [plausibleIgnore, setPlausibleIgnore] = useState<boolean>(readPlausibleIgnore);
  const [sfState, setSfState] = useState<CloudCacheState>(() =>
    typeof document === 'undefined' ? 'unknown' : readCloudCacheAnalytics(),
  );
  const [gpc, setGpc] = useState<boolean>(() => isGpcActive());

  useEffect(() => {
    applyPageMeta(PAGE_META.privacySettings);
  }, []);

  // When geo resolves and the user has no saved record yet, sync the toggles
  // to the effective default (geo + SF cookie) so the UI reflects what's
  // actually in force.
  useEffect(() => {
    if (loading || stored) return;
    const defaults = getEffectiveDefaults(country);
    setAnalyticsStorage(defaults.analytics);
    setMarketingStorage(defaults.marketing);
  }, [loading, country, stored]);

  // Re-read the SF cookie and GPC state whenever the tab regains focus. A
  // user who opens SF's DNS page in another tab, changes their setting,
  // and comes back should see the updated lock reflected without a full
  // reload.
  useEffect(() => {
    const refresh = () => {
      setSfState(readCloudCacheAnalytics());
      setGpc(isGpcActive());
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  // Base each save on the saved record if present, else the effective
  // default (geo + SF cookie), so flipping one tier preserves the other
  // at its correct current value.
  const baselinePrefs = () => readConsent()?.prefs ?? getEffectiveDefaults(country);

  // Each handler preserves the other tiers so flipping one doesn't reset another.
  //
  // Reload-on-revoke: turning a tier OFF while its tracker script is already
  // resident must reload the page — the unload path only removes the script
  // element, and an already-executed tracker (Plausible's history hook,
  // DEEL's Adobe machinery) keeps running until the page unloads. The record
  // is persisted before the reload, so the reloaded page shows the same
  // toggle state with the tracker genuinely gone. Capture presence BEFORE
  // saveConsent — its sync path removes the element. GTM/GA needs no reload
  // (gtag re-checks consent per hit), and the consent-log POST survives the
  // reload (fetch keepalive).
  const onToggleAnalyticsStorage = (next: boolean) => {
    setAnalyticsStorage(next);
    const mustReload = !next && isPlausibleScriptPresent();
    saveConsent(
      { ...baselinePrefs(), necessary: true, analytics: next },
      'updated',
      'privacy-settings',
    );
    if (mustReload) window.location.reload();
  };

  const onToggleMarketingStorage = (next: boolean) => {
    setMarketingStorage(next);
    const mustReload = !next && (isDeelScriptPresent() || isSfGtmPresent());
    saveConsent(
      { ...baselinePrefs(), necessary: true, marketing: next },
      'updated',
      'privacy-settings',
    );
    if (mustReload) window.location.reload();
  };

  const onTogglePlausibleIgnore = (next: boolean) => {
    setPlausibleIgnore(next);
    writePlausibleIgnore(next);
    if (next) {
      try { window.__unloadPlausible?.(); } catch { /* stripped in dev */ }
    } else {
      try { window.__loadPlausible?.(); } catch { /* stripped in dev */ }
    }
  };

  // Compose lock state for each toggle.
  const analyticsLock: LockSource = gpc
    ? 'gpc'
    : country === 'US' && sfState !== 'unknown'
      ? 'sf-cookie'
      : null;
  const marketingLock: LockSource = gpc ? 'gpc' : null;
  const plausibleLock: LockSource = gpc
    ? 'gpc'
    : country === 'US' && sfState === 'denied'
      ? 'sf-cookie'
      : null;

  // What state to display when a lock is active — reflects the runtime
  // state that the actual loaders are enforcing, not the user's stored
  // preference.
  const analyticsDisplayed = analyticsLock === 'gpc'
    ? false
    : analyticsLock === 'sf-cookie'
      ? sfState === 'granted'
      : analyticsStorage;
  const marketingDisplayed = marketingLock === 'gpc' ? false : marketingStorage;
  const plausibleDisplayed = plausibleLock === 'gpc' || plausibleLock === 'sf-cookie'
    ? true
    : plausibleIgnore;

  return (
    <section className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="font-display text-3xl uppercase tracking-widest text-brand-redAccent">
        Privacy Settings
      </h1>

      <div className="mt-6 space-y-4 leading-relaxed text-white/80">
        <p>
          Manage how this site uses cookies and similar tracking technologies
          to support essential functionality, measure site performance, and
          deliver advertising. Your choices are stored in your browser and
          apply to this device only.
        </p>
        {gpc && (
          <p className="rounded-md border border-brand-redAccent/40 bg-brand-redAccent/10 px-4 py-3 text-sm text-white/90">
            A Global Privacy Control (GPC) signal was detected from your
            browser. We are honoring it: analytics and advertising storage
            are denied, and these toggles are locked until you disable GPC
            in your browser settings.
          </p>
        )}
        {!gpc && country === 'US' && sfState !== 'unknown' && (
          <p className="rounded-md border border-white/20 bg-white/5 px-4 py-3 text-sm text-white/80">
            Your State Farm Do Not Sell or Share preference is active on
            this device. The Analytics toggle below reflects that choice and
            is locked here; to change it, use the{' '}
            {/* Same-tab on purpose, matching the footer DNS link: the
                opt-out flow must stay in the user's current context, and
                returning via a fresh pageload re-runs the boot gates so the
                updated cookie takes effect immediately. */}
            <a
              href="https://www.statefarm.com/privacy-security/do-not-share-my-personal-information"
              className="text-brand-redAccent underline-offset-2 hover:underline"
            >
              State Farm Do Not Sell or Share
            </a>{' '}
            page.
          </p>
        )}
      </div>

      <div className="mt-8 space-y-4">
        <NecessaryRow />
        <ToggleRow
          label="Allow analytics storage"
          hint={
            analyticsLock === 'gpc'
              ? 'Locked to OFF while your browser is sending a Global Privacy Control signal. Disable GPC in your browser to change this here.'
              : analyticsLock === 'sf-cookie'
                ? 'Set by your State Farm Do Not Sell or Share preference. To change, use that page.'
                : 'When on, this site may use cookies and similar device storage for analytics. Turn it off to opt out — your choice is saved on this device/browser combination and respected on every future visit.'
          }
          checked={analyticsDisplayed}
          disabled={analyticsLock !== null}
          onChange={onToggleAnalyticsStorage}
        />
        <ToggleRow
          label="Allow advertising / marketing storage"
          hint={
            marketingLock === 'gpc'
              ? 'Locked to OFF while your browser is sending a Global Privacy Control signal. Disable GPC in your browser to change this here.'
              : "When on, this site loads State Farm's DEEL advertising/measurement tag and may use cookies and device storage for marketing. Turn it off to opt out — the script is removed immediately."
          }
          checked={marketingDisplayed}
          disabled={marketingLock !== null}
          onChange={onToggleMarketingStorage}
        />
        <ToggleRow
          label="Don't count my visit in anonymous analytics"
          hint={
            plausibleLock === 'gpc'
              ? 'Locked to ON while your browser is sending a Global Privacy Control signal. Anonymous analytics is fully suppressed. Disable GPC in your browser to change this here.'
              : plausibleLock === 'sf-cookie'
                ? 'Anonymous analytics is fully suppressed because you opted out on the State Farm Do Not Sell or Share page. To change, use that page.'
                : "This site uses Plausible for cookieless audience measurement. Visits are aggregated server-side with no device storage. If you'd rather not be counted at all, turn this on — your preference is saved on this device/browser combination and respected on every future visit."
          }
          checked={plausibleDisplayed}
          disabled={plausibleLock !== null}
          onChange={onTogglePlausibleIgnore}
        />
      </div>

      <div className="mt-10 space-y-4 text-sm leading-relaxed text-white/70">
        <h2 className="font-heading text-base uppercase tracking-wider text-white/90">
          When you create an account
        </h2>
        <p>
          Once you register, we attach your account identifier to analytics
          events so we can measure feature engagement and our sign-up funnel.
          This processing is based on our legitimate interest in improving
          the experience (GDPR Article 6(1)(f)) and is disclosed in our{' '}
          <a href="/privacy" className="text-brand-redAccent underline-offset-2 hover:underline">
            Privacy Notice
          </a>
          . You can object to it at any time using the Privacy Settings toggle
          above; turning off anonymous counting also suppresses
          account-attributed events (the underlying tracker simply does not
          run).
        </p>
        <p>
          For Do Not Sell or Share, see the link in our footer.
        </p>
      </div>
    </section>
  );
}

type ToggleRowProps = {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
};

function ToggleRow({ label, hint, checked, disabled = false, onChange }: ToggleRowProps) {
  const Wrapper = disabled ? 'div' : 'label';
  return (
    <Wrapper
      className={
        disabled
          ? 'flex items-start justify-between gap-4 rounded-lg border border-white/10 bg-white/5 px-4 py-4 opacity-80'
          : 'flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-white/10 bg-white/5 px-4 py-4 transition hover:bg-white/10'
      }
    >
      <span className="flex flex-1 flex-col">
        <span className="font-semibold text-white">{label}</span>
        <span className="mt-1 text-xs text-white/60">{hint}</span>
      </span>
      <span className="relative inline-flex h-6 w-11 flex-none items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          readOnly={disabled}
          onChange={disabled ? undefined : (e) => onChange(e.target.checked)}
          aria-label={disabled ? `${label} (locked)` : undefined}
        />
        <span
          aria-hidden="true"
          className={
            disabled
              ? 'absolute inset-0 rounded-full bg-white/20 transition peer-checked:bg-brand-red/60'
              : 'absolute inset-0 rounded-full bg-white/20 transition peer-checked:bg-brand-red peer-focus-visible:ring-2 peer-focus-visible:ring-white peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-brand-deep'
          }
        />
        <span
          aria-hidden="true"
          className={
            disabled
              ? 'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white/85 shadow transition peer-checked:translate-x-5'
              : 'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5'
          }
        />
      </span>
    </Wrapper>
  );
}

/**
 * Always-on disclosure row for the "strictly necessary" tier. Mirrors the
 * ToggleRow visual but the control is disabled-on, signaling to the user that
 * (a) these items run unconditionally, and (b) that's intentional — they're
 * what makes login / session / consent itself work.
 */
function NecessaryRow() {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-white/10 bg-white/5 px-4 py-4">
      <span className="flex flex-1 flex-col">
        <span className="font-semibold text-white">Strictly necessary</span>
        <span className="mt-1 text-xs text-white/60">
          Always on. Includes the session cookie used to keep you logged in,
          the refresh token used to keep your session alive, the record of
          this consent decision, and a short-lived cache of your region used
          to show the right eligibility notice on the Official Rules page.
          These are required to make the site work and can't be turned off.
        </span>
      </span>
      <span className="relative inline-flex h-6 w-11 flex-none items-center">
        <input
          type="checkbox"
          className="peer sr-only"
          checked
          disabled
          readOnly
          aria-label="Strictly necessary — always on"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-brand-red/60"
        />
        <span
          aria-hidden="true"
          className="absolute left-0.5 top-0.5 h-5 w-5 translate-x-5 rounded-full bg-white/85 shadow"
        />
      </span>
    </div>
  );
}
